/**
 * TimerPreview.jsx
 *
 * Renders the live countdown preview used by the premium timer builder.
 */
import React, { useEffect, useRef, useState } from "react";
import { Text } from "@shopify/polaris";

function pad(value) {
  return String(value).padStart(2, "0");
}

function formatCompactCountdown({ days, hours, minutes, seconds }) {
  const time = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${pad(days)}d ${time}` : time;
}

function formatDemoCountdown(totalSeconds) {
  const safeSeconds = Math.max(0, totalSeconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}

function previewBackground(design) {
  if (design.backgroundMode === "gradient") {
    return `linear-gradient(${Number(design.gradientAngle ?? 90)}deg, ${design.gradientStartColor}, ${design.gradientEndColor})`;
  }
  return design.backgroundColor;
}

function productCardFlagLabel(content) {
  return content.eyebrow || content.title || content.timerLabel || "Ends in";
}

export default function TimerPreview({
  timerEnabled,
  timerEndTime,
  timerText,
  timerColor,
  timerFontSize,
  hideBranding,
  content = null,
  design = null,
  previewMode = false,
  presentation = "default",
  viewport = "desktop",
  demoDurationSeconds = null,
}) {
  const [countdown, setCountdown] = useState("--:--:--");
  const [parts, setParts] = useState({ days: "--", hours: "--", minutes: "--", seconds: "--" });
  const [expired, setExpired] = useState(false);
  const intervalRef = useRef(null);

  const previewContent = {
    eyebrow: content?.eyebrow || "",
    title: content?.title || "",
    subtitle: content?.subtitle || "",
    timerLabel: content?.timerLabel ?? timerText ?? "Sale ends in:",
    ctaLabel: content?.ctaLabel || "",
    ctaUrl: content?.ctaUrl || "#",
    ctaBehavior: content?.ctaBehavior || "visible",
  };

  const previewDesign = {
    backgroundMode: design?.backgroundMode || "solid",
    backgroundColor: design?.backgroundColor || "#fff8f8",
    gradientStartColor: design?.gradientStartColor || design?.backgroundColor || "#fff8f8",
    gradientEndColor: design?.gradientEndColor || design?.backgroundColor || "#fff8f8",
    gradientAngle: Number(design?.gradientAngle ?? 90),
    textColor: design?.textColor || timerColor || "#ff0000",
    accentColor: design?.accentColor || timerColor || "#ff0000",
    eyebrowColor: design?.eyebrowColor || design?.accentColor || timerColor || "#ff0000",
    titleColor: design?.titleColor || design?.textColor || "#202223",
    subtitleColor: design?.subtitleColor || design?.textColor || "#202223",
    timerColor: design?.timerColor || design?.textColor || "#202223",
    ctaBackgroundColor: design?.ctaBackgroundColor || design?.accentColor || timerColor || "#ff0000",
    ctaTextColor: design?.ctaTextColor || "#ffffff",
    brandingBackgroundColor: design?.brandingBackgroundColor || design?.accentColor || "#bf0711",
    brandingTextColor: design?.brandingTextColor || "#ffffff",
    fontSize: Number(design?.fontSize || timerFontSize || 20),
    eyebrowFontSize: Number(design?.eyebrowFontSize || 13),
    titleFontSize: Number(design?.titleFontSize || design?.fontSize || timerFontSize || 20),
    subtitleFontSize: Number(design?.subtitleFontSize || 15),
    timerFontSize: Number(design?.timerFontSize || design?.fontSize || timerFontSize || 24),
    ctaFontSize: Number(design?.ctaFontSize || 14),
    borderRadius: Number(design?.borderRadius ?? 6),
    layout: design?.layout || "vertical",
    displayStyle: design?.displayStyle || "compact",
    blockBorderRadius: Number(design?.blockBorderRadius ?? design?.borderRadius ?? 6),
    timeBoxBackgroundColor: design?.timeBoxBackgroundColor || "#ffffff",
    timeBoxTextColor: design?.timeBoxTextColor || design?.timerColor || design?.textColor || "#202223",
    timeBoxBorderColor: design?.timeBoxBorderColor || "transparent",
    timeBoxBorderStyle: design?.timeBoxBorderStyle || "none",
    timeBoxBorderWidth: Number(design?.timeBoxBorderWidth ?? 0),
    flagSideTailLength: Number(design?.flagSideTailLength ?? 0),
    showDays: design?.showDays !== false,
    showHours: design?.showHours !== false,
    showMinutes: design?.showMinutes !== false,
    showSeconds: design?.showSeconds !== false,
    showTimeLabels: design?.showTimeLabels !== false,
    paddingTop: Number(design?.paddingTop ?? 12),
    paddingBottom: Number(design?.paddingBottom ?? 12),
  };

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setExpired(false);

    const demoSeconds = Number(demoDurationSeconds);
    const hasDemoDuration = Number.isFinite(demoSeconds) && demoSeconds > 0;

    if (!timerEnabled || (!timerEndTime && !hasDemoDuration)) {
      setCountdown("--:--:--");
      setParts({ days: "--", hours: "--", minutes: "--", seconds: "--" });
      return undefined;
    }

    const endTime = hasDemoDuration ? Date.now() + demoSeconds * 1000 : new Date(timerEndTime).getTime();
    if (Number.isNaN(endTime)) {
      setCountdown("Invalid date");
      return undefined;
    }

    function tick() {
      const remaining = Math.floor((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setCountdown("00:00:00");
        setParts({ days: "00", hours: "00", minutes: "00", seconds: "00" });
        setExpired(true);
        clearInterval(intervalRef.current);
        return;
      }

      const d = Math.floor(remaining / 86400);
      const h = Math.floor((remaining % 86400) / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      setCountdown(
        hasDemoDuration
          ? formatDemoCountdown(remaining)
          : formatCompactCountdown({ days: d, hours: h, minutes: m, seconds: s })
      );
      setParts({ days: pad(d), hours: pad(h), minutes: pad(m), seconds: pad(s) });
    }

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => clearInterval(intervalRef.current);
  }, [demoDurationSeconds, timerEnabled, timerEndTime]);

  if (!timerEnabled) {
    return (
      <div style={{ padding: "16px", textAlign: "center", color: "#888" }}>
        <Text>Timer is disabled.</Text>
      </div>
    );
  }

  const isProductCardFlag = presentation === "product-card-flag";
  const isStrip = presentation === "strip";
  const isMobile = viewport === "mobile";
  const isInline = !isMobile && (isStrip || previewDesign.layout === "horizontal");
  const previewBorderRadius = isStrip ? Math.max(previewDesign.borderRadius, 12) : previewDesign.borderRadius;
  const wrapperStyle = {
    "--qut-preview-accent": previewDesign.accentColor,
    "--qut-preview-background": previewDesign.backgroundColor,
    padding: `${previewDesign.paddingTop}px 20px ${previewDesign.paddingBottom}px`,
    border: isStrip ? "0" : `2px solid ${previewDesign.accentColor}`,
    borderRadius: `${previewBorderRadius}px`,
    background: previewBackground(previewDesign),
    color: previewDesign.textColor,
    fontSize: `${previewDesign.fontSize}px`,
    opacity: expired ? 0.5 : 1,
  };

  const labelStyle = {
    fontSize: `${previewDesign.eyebrowFontSize}px`,
    fontWeight: 800,
    color: previewDesign.eyebrowColor,
    textTransform: "uppercase",
  };
  const showCta = previewContent.ctaLabel && previewContent.ctaBehavior !== "hidden";
  const disableCtaAfterExpiry = previewContent.ctaBehavior === "disabled-after-expiry" && expired;
  const ctaStyle = {
    borderRadius: Math.max(4, previewDesign.borderRadius / 2),
    background: previewDesign.ctaBackgroundColor,
    color: previewDesign.ctaTextColor,
    fontSize: `${previewDesign.ctaFontSize}px`,
    fontWeight: 700,
    padding: "6px 10px",
    textDecoration: "none",
    opacity: disableCtaAfterExpiry ? 0.65 : 1,
    cursor: disableCtaAfterExpiry ? "not-allowed" : "pointer",
  };
  const countdownUnits = [
    ["days", "DAYS", previewDesign.showDays],
    ["hours", "HOURS", previewDesign.showHours],
    ["minutes", "MINS", previewDesign.showMinutes],
    ["seconds", "SECS", previewDesign.showSeconds],
  ].filter(([, , visible]) => visible);
  const visibleCountdownUnits = countdownUnits.length > 0 ? countdownUnits : [["seconds", "SECS", true]];

  if (isProductCardFlag) {
    const tailLength = Math.max(0, previewDesign.flagSideTailLength || 0);
    const tailBorderWidth =
      previewDesign.timeBoxBorderStyle === "none" ? 1 : Math.max(1, previewDesign.timeBoxBorderWidth || 1);
    const tailBorderColor =
      previewDesign.timeBoxBorderStyle === "none"
        ? "rgba(255,255,255,0.72)"
        : previewDesign.timeBoxBorderColor || "rgba(255,255,255,0.72)";
    const tailBorderStyle = previewDesign.timeBoxBorderStyle === "none" ? "solid" : previewDesign.timeBoxBorderStyle;

    return (
      <div className={`qut-card-preview ${isMobile ? "qut-card-preview--mobile" : ""}`} role="timer" aria-live="polite">
        <div className="qut-card-preview__media">
          <div
            className="qut-card-preview__flag"
            style={{
              background: previewBackground(previewDesign),
              color: previewDesign.textColor,
              borderRadius: `${Math.max(8, previewDesign.borderRadius)}px`,
              border:
                previewDesign.timeBoxBorderStyle === "none"
                  ? "0 solid transparent"
                  : `${tailBorderWidth}px ${tailBorderStyle} ${tailBorderColor}`,
              opacity: expired ? 0.5 : 1,
            }}
          >
            {tailLength > 0 ? (
              <>
                <span
                  className="qut-card-preview__tail qut-card-preview__tail--left"
                  style={{
                    height: `${tailLength}px`,
                    borderLeft: `${tailBorderWidth}px ${tailBorderStyle} ${tailBorderColor}`,
                  }}
                />
                <span
                  className="qut-card-preview__tail qut-card-preview__tail--right"
                  style={{
                    height: `${tailLength}px`,
                    borderRight: `${tailBorderWidth}px ${tailBorderStyle} ${tailBorderColor}`,
                  }}
                />
              </>
            ) : null}
            <span
              className="qut-card-preview__flag-label"
              style={{ color: previewDesign.eyebrowColor, fontSize: `${previewDesign.eyebrowFontSize}px` }}
            >
              {productCardFlagLabel(previewContent)}
            </span>
            <strong
              className="qut-card-preview__flag-countdown"
              style={{ color: previewDesign.timerColor, fontSize: `${previewDesign.timerFontSize}px` }}
            >
              {countdown}
            </strong>
          </div>
        </div>
        <div className="qut-card-preview__details">
          <span className="qut-card-preview__eyebrow" style={{ color: previewDesign.accentColor }}>
            Product card
          </span>
          <strong className="qut-card-preview__title">Minimalist Ceramic Watch</strong>
          <span className="qut-card-preview__price">$129.00</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`qut-preview qut-preview--${isInline ? "horizontal" : "vertical"} ${
        isStrip ? "qut-preview--strip" : ""
      } ${isMobile ? "qut-preview--mobile" : ""}`}
      style={wrapperStyle}
      aria-live="polite"
      role="timer"
    >
      <span className="qut-preview__content">
        {previewContent.eyebrow ? <span style={labelStyle}>{previewContent.eyebrow}</span> : null}
        {previewContent.title ? (
          <strong style={{ color: previewDesign.titleColor, fontSize: `${previewDesign.titleFontSize}px` }}>
            {previewContent.title}
          </strong>
        ) : null}
        {previewContent.subtitle ? (
          <span
            style={{
              color: previewDesign.subtitleColor,
              fontSize: `${previewDesign.subtitleFontSize}px`,
              fontWeight: 500,
            }}
          >
            {previewContent.subtitle}
          </span>
        ) : null}
        <span
          style={{
            fontSize: `${Math.max(11, previewDesign.subtitleFontSize - 1)}px`,
            fontWeight: 700,
            color: previewDesign.accentColor,
          }}
        >
          {previewContent.timerLabel}
        </span>
      </span>
      {previewDesign.displayStyle === "blocks" ? (
        <span
          className="qut-preview__blocks"
          style={{
            color: previewDesign.timerColor,
            fontSize: `${previewDesign.timerFontSize}px`,
            gridTemplateColumns: `repeat(${visibleCountdownUnits.length}, minmax(42px, 1fr))`,
          }}
        >
          {visibleCountdownUnits.map(([key, label]) => (
            <span className="qut-preview__block" key={key}>
              <strong
                style={{
                  background: previewDesign.timeBoxBackgroundColor,
                  borderColor: previewDesign.timeBoxBorderColor,
                  borderStyle: previewDesign.timeBoxBorderStyle,
                  borderWidth: `${previewDesign.timeBoxBorderStyle === "none" ? 0 : previewDesign.timeBoxBorderWidth}px`,
                  borderRadius: `${previewDesign.blockBorderRadius}px`,
                  color: previewDesign.timeBoxTextColor,
                }}
              >
                {parts[key]}
              </strong>
              {previewDesign.showTimeLabels ? <small>{label}</small> : null}
            </span>
          ))}
        </span>
      ) : (
        <span
          className="qut-preview__countdown"
          style={{ color: previewDesign.timerColor, fontSize: `${previewDesign.timerFontSize}px` }}
        >
          {countdown}
        </span>
      )}
      {showCta ? (
        disableCtaAfterExpiry ? (
          <button className="qut-preview__cta" type="button" disabled style={ctaStyle}>
            {previewContent.ctaLabel}
          </button>
        ) : previewMode ? (
          <span
            className="qut-preview__cta"
            style={ctaStyle}
          >
            {previewContent.ctaLabel}
          </span>
        ) : (
          <a
            className="qut-preview__cta"
            href={previewContent.ctaUrl || "#"}
            style={ctaStyle}
          >
            {previewContent.ctaLabel}
          </a>
        )
      ) : null}
      {!hideBranding && (
        <span
          className="qut-preview__branding"
          style={{
            background: previewDesign.brandingBackgroundColor,
            color: previewDesign.brandingTextColor,
          }}
        >
          Powered by Quick Urgency Timer
        </span>
      )}
    </div>
  );
}
